import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "content-grid" }
const _hoisted_2 = ["onSubmit"]
const _hoisted_3 = { id: "address-list" }
const _hoisted_4 = ["id"]
const _hoisted_5 = { class: "form__duo-fields" }
const _hoisted_6 = { class: "form__submit btn btn--fullscreen" }
const _hoisted_7 = { class: "content-grid content-grid--double" }

import {computed, ref} from "vue";
import {useI18n} from "vue3-i18n";
import {useMemberStore} from "@/stores/memberStore";
import {useAddressService, useMemberService} from "@/inversify.config";
import {mustMatchPhoneNumberFormat, mustMatchZipCodeFormat, required,} from "@/validation/rules";
import {notifyError, notifySuccess} from "@/notify";
import {Role} from "@/types";
import {Status} from "@/validation";
import {SucceededOrNotResponse} from "@/types/responses/succeededOrNotResponse";
import Card from "@/components/layout/Card.vue";
import FormInput from "@/components/forms/FormInput.vue";
import FormRow from "@/components/forms/FormRow.vue";
import ChangePassword from "@/views/ChangePassword.vue";
import ChangeEmail from "@/views/ChangeEmail.vue";
import MembershipCertificate from "@/components/memberships/MembershipCertificate.vue";
import FormSelect from "@/components/forms/FormSelect.vue";
import FormReadonlyField from "@/components/forms/FormReadonlyField.vue";
import {IFilterAddressesRequest, IGetAddressRequest} from "@/types/requests";
import {IAddress} from "@/types/responses";


export default /*#__PURE__*/_defineComponent({
  __name: 'Account',
  setup(__props) {

const {t, getLocale} = useI18n()
const memberStore = useMemberStore();
const memberService = useMemberService();
const addressService = useAddressService()

const member = computed(() => memberStore.getMember);
const updateMemberResponse = ref<SucceededOrNotResponse>();
const addressOptions = ref<IAddress[]>([])

const rolesAllowedToEditJobTitle = [Role.CorporateAdmin, Role.RegularSyndicateAdmin, Role.RegularSyndicateManager, Role.RegularIndividual]
const rolesThatCanHaveAnyJobTitle = [Role.CorporateAdmin, Role.RegularIndividual]

const canEditJobTitle = computed(() => memberStore.hasOneOfTheseRoles(rolesAllowedToEditJobTitle))
const canHaveAnyJobTitle = computed(() => memberStore.hasOneOfTheseRoles(rolesThatCanHaveAnyJobTitle))
const jobTitles = [
  {
    label: t('member.jobTitle.administrator.label'),
    name: t('member.jobTitle.administrator.value')
  },
  {
    label: t('member.jobTitle.manager.label'),
    name: t('member.jobTitle.manager.value')
  },
  {
    label: t('member.jobTitle.coowner.label'),
    name: t('member.jobTitle.coowner.value')
  },
]

const formInputs = ref<(typeof FormInput)[]>([])
const inputValidationStatuses: any = {}

function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref))
    formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid;
}

async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  if (Object.values(inputValidationStatuses).some((x) => x === false)) {
    notifyError(t("global.formErrorNotification"));
    return;
  }

  updateMemberResponse.value = (await memberService.updateAuthenticatedMember(
      member.value
  )) as SucceededOrNotResponse;

  updateMemberResponse.value?.succeeded
      ? notifySuccess(t("account.form.successMessage"))
      : notifyError(t("account.form.failedMessage"));
}

async function handleInput(inputValue: string) {
  let selectedOptions = addressOptions.value.filter(x => x.text == inputValue)
  if (selectedOptions.length > 0) {
    let request = {
      id: selectedOptions[0].id,
      language: getLocale()
    } as IGetAddressRequest
    let response = await addressService.getAddress(request)
    member.value.street = response.detailedAddress.street
    member.value.city = response.detailedAddress.city
    member.value.zipCode = response.detailedAddress.zipCode.replace(' ', '')
    return;
  }
  await loadMatchingAddresses(inputValue)
}

async function loadMatchingAddresses(inputValue: string) {
  let request = {
    street: inputValue,
    language: getLocale()
  } as IFilterAddressesRequest
  let addresses = await addressService.filterAddresses(request)
  addressOptions.value = addresses.matchingAddresses
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (member.value)
      ? (_openBlock(), _createBlock(Card, {
          key: 0,
          title: _unref(t)('routes.account.name')
        }, {
          default: _withCtx(() => [
            _createElementVNode("form", {
              class: "form",
              novalidate: "",
              onSubmit: _withModifiers(handleSubmit, ["prevent"])
            }, [
              _createVNode(FormRow, { "with-three-columns": true }, {
                default: _withCtx(() => [
                  _createVNode(FormInput, {
                    ref: addFormInputRef,
                    modelValue: member.value.firstName,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((member.value.firstName) = $event)),
                    label: _unref(t)('global.firstName'),
                    name: "firstName",
                    type: "text",
                    onValidated: handleValidation
                  }, null, 8, ["modelValue", "label"]),
                  _createVNode(FormInput, {
                    ref: addFormInputRef,
                    modelValue: member.value.lastName,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((member.value.lastName) = $event)),
                    label: _unref(t)('global.lastName'),
                    name: "lastName",
                    type: "text",
                    onValidated: handleValidation
                  }, null, 8, ["modelValue", "label"]),
                  (canEditJobTitle.value && canHaveAnyJobTitle.value)
                    ? (_openBlock(), _createBlock(FormInput, {
                        key: 0,
                        ref: addFormInputRef,
                        modelValue: member.value.jobTitle,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((member.value.jobTitle) = $event)),
                        label: _unref(t)('account.form.jobTitle'),
                        name: "jobTitle",
                        type: "text",
                        onValidated: handleValidation
                      }, null, 8, ["modelValue", "label"]))
                    : _createCommentVNode("", true),
                  (canEditJobTitle.value && !canHaveAnyJobTitle.value)
                    ? (_openBlock(), _createBlock(FormSelect, {
                        key: 1,
                        ref: addFormInputRef,
                        modelValue: member.value.jobTitle,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((member.value.jobTitle) = $event)),
                        label: _unref(t)('global.jobTitle'),
                        options: jobTitles,
                        name: "jobTitle",
                        onValidated: handleValidation
                      }, null, 8, ["modelValue", "label"]))
                    : _createCommentVNode("", true),
                  (!canEditJobTitle.value)
                    ? (_openBlock(), _createBlock(FormReadonlyField, {
                        key: 2,
                        modelValue: member.value.jobTitle,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((member.value.jobTitle) = $event)),
                        label: _unref(t)('global.jobTitle'),
                        name: "jobTitle"
                      }, null, 8, ["modelValue", "label"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              _createVNode(FormRow, { "with-three-columns": true }, {
                default: _withCtx(() => [
                  _createVNode(FormInput, {
                    modelValue: member.value.apartment,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((member.value.apartment) = $event)),
                    label: _unref(t)('global.apartment'),
                    rules: [],
                    name: "apartment",
                    type: "number",
                    onValidated: handleValidation
                  }, null, 8, ["modelValue", "label"]),
                  _createVNode(FormInput, {
                    ref: addFormInputRef,
                    modelValue: member.value.street,
                    "onUpdate:modelValue": [
                      _cache[6] || (_cache[6] = ($event: any) => ((member.value.street) = $event)),
                      handleInput
                    ],
                    label: _unref(t)('global.street'),
                    list: 'address-list',
                    name: "street",
                    type: "text",
                    onValidated: handleValidation
                  }, {
                    "before-input": _withCtx(() => [
                      _createElementVNode("datalist", _hoisted_3, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(addressOptions.value, (address) => {
                          return (_openBlock(), _createElementBlock("option", {
                            id: address.id,
                            key: address.id
                          }, _toDisplayString(address.text), 9, _hoisted_4))
                        }), 128))
                      ])
                    ]),
                    _: 1
                  }, 8, ["modelValue", "label"]),
                  _createVNode(FormInput, {
                    ref: addFormInputRef,
                    modelValue: member.value.city,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((member.value.city) = $event)),
                    label: _unref(t)('global.city'),
                    name: "city",
                    type: "text",
                    onValidated: handleValidation
                  }, null, 8, ["modelValue", "label"])
                ]),
                _: 1
              }),
              _createVNode(FormRow, { "with-three-columns": true }, {
                default: _withCtx(() => [
                  _createVNode(FormInput, {
                    ref: addFormInputRef,
                    modelValue: member.value.zipCode,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((member.value.zipCode) = $event)),
                    label: _unref(t)('global.zipCode'),
                    rules: [_unref(required), _unref(mustMatchZipCodeFormat)],
                    name: "zipCode",
                    type: "zip",
                    onValidated: handleValidation
                  }, null, 8, ["modelValue", "label", "rules"]),
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(FormInput, {
                      ref: addFormInputRef,
                      modelValue: member.value.phoneNumber,
                      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((member.value.phoneNumber) = $event)),
                      label: _unref(t)('global.phoneNumber'),
                      rules: [_unref(required), _unref(mustMatchPhoneNumberFormat)],
                      name: "phoneNumber",
                      type: "tel",
                      onValidated: handleValidation
                    }, null, 8, ["modelValue", "label", "rules"]),
                    _createVNode(FormInput, {
                      ref: addFormInputRef,
                      modelValue: member.value.phoneExtension,
                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((member.value.phoneExtension) = $event)),
                      label: _unref(t)('global.phoneExtension'),
                      rules: [],
                      name: "phoneExtension",
                      type: "number",
                      onValidated: handleValidation
                    }, null, 8, ["modelValue", "label"])
                  ])
                ]),
                _: 1
              }),
              _createElementVNode("button", _hoisted_6, _toDisplayString(_unref(t)("global.save")), 1)
            ], 40, _hoisted_2)
          ]),
          _: 1
        }, 8, ["title"]))
      : _createCommentVNode("", true),
    _createVNode(MembershipCertificate),
    _createElementVNode("div", _hoisted_7, [
      _createVNode(ChangePassword),
      _createVNode(ChangeEmail)
    ])
  ]))
}
}

})